const SPLAT_PARAM = "*";

const chromelessPaths = new Set<string>();
const desktopChromelessPaths = new Set<string>();
const listeners = new Set<() => void>();
let version = 0;

const normalize = (pathname: string): string => pathname.replace(/\/+$/, "") || "/";

const decodeSegment = (segment: string): string => {
	try {
		return decodeURIComponent(segment);
	} catch {
		return segment;
	}
};

const collectNamedParamValues = (params: Readonly<Record<string, string | undefined>>): string[] =>
	Object.entries(params)
		.filter(([name, value]) => name !== SPLAT_PARAM && Boolean(value))
		.map(([, value]) => value as string);

const notifyListeners = (): void => {
	version += 1;
	for (const listener of listeners) listener();
};

export const registerChromelessPath = (pathname: string): void => {
	const normalized = normalize(pathname);
	if (chromelessPaths.has(normalized)) return;
	chromelessPaths.add(normalized);
	notifyListeners();
};

export const unregisterChromelessPath = (pathname: string): void => {
	const normalized = normalize(pathname);
	if (!chromelessPaths.has(normalized)) return;
	chromelessPaths.delete(normalized);
	notifyListeners();
};

export const isChromelessPath = (pathname: string): boolean => chromelessPaths.has(normalize(pathname));

export const resolveChromelessRoutePaths = (
	pathname: string,
	params: Readonly<Record<string, string | undefined>>
): string[] => {
	const unconsumedValues = collectNamedParamValues(params);
	const segments = normalize(pathname).split("/");
	const paths = [pathname];

	while (segments.length > 1) {
		const valueIndex = unconsumedValues.indexOf(decodeSegment(segments[segments.length - 1]));
		if (valueIndex === -1) break;
		unconsumedValues.splice(valueIndex, 1);
		segments.pop();
		paths.push(normalize(segments.join("/")));
	}

	return paths;
};

export const registerDesktopChromelessPath = (pathname: string): void => {
	const normalized = normalize(pathname);
	if (desktopChromelessPaths.has(normalized)) return;
	desktopChromelessPaths.add(normalized);
	notifyListeners();
};

export const unregisterDesktopChromelessPath = (pathname: string): void => {
	const normalized = normalize(pathname);
	if (!desktopChromelessPaths.has(normalized)) return;
	desktopChromelessPaths.delete(normalized);
	notifyListeners();
};

export const isDesktopChromelessPath = (pathname: string): boolean => desktopChromelessPaths.has(normalize(pathname));

export const subscribeChromeless = (listener: () => void): (() => void) => {
	listeners.add(listener);
	return () => {
		listeners.delete(listener);
	};
};

export const getChromelessVersion = (): number => version;
